import { useQuery } from '@tanstack/react-query';
import { SponsorshipService } from '@/services/sponsorship.service';
import { CalendarSponsorship } from '@/types/booking.types';
import { shortCacheQueryOptions } from '@/config/react-query.config';
import { calendarSponsorshipKeys } from './useCalendarSponsorship';

export interface CalendarSponsorshipEntry {
  calendarId: string;
  locationId: string;
}

/**
 * Batch sponsorship lookup: one API call per unique locationId.
 * Accepts entries with per-booking locationId so drop-in members get
 * sponsorships scoped to the visited location, not their home location.
 * Returns a map keyed by calendarId.
 */
export const useCalendarSponsorships = (entries: CalendarSponsorshipEntry[]) => {
  const stableKey = [...entries]
    .sort((a, b) => a.calendarId.localeCompare(b.calendarId))
    .map((e) => `${e.calendarId}:${e.locationId}`)
    .join(',');

  const query = useQuery<Record<string, CalendarSponsorship | null>, Error>({
    queryKey: [...calendarSponsorshipKeys.all, 'batch', stableKey],
    queryFn: async () => {
      if (!entries.length) return {};
      const byLocation = new Map<string, string[]>();
      for (const { calendarId, locationId } of entries) {
        const ids = byLocation.get(locationId) ?? [];
        ids.push(calendarId);
        byLocation.set(locationId, ids);
      }
      const results = await Promise.all(
        Array.from(byLocation.entries()).map(([locationId, ids]) =>
          SponsorshipService.getCalendarSponsorships(ids, locationId),
        ),
      );
      return Object.assign({}, ...results);
    },
    enabled: entries.length > 0,
    ...shortCacheQueryOptions,
  });

  return { byCalendarId: query.data ?? {} };
};
